import React, { useState } from "react";
import { Icon, Message } from "semantic-ui-react";
import classNames from "classnames";
import { map, filter, groupBy, sortBy, size, uniq, compact } from "lodash";
import { useHistory } from "react-router-dom";
import { HeaderPage } from "../../components/Admin";
import { useOrder, useNow, usePolling } from "../../hooks";
import { ORDER_STATUS } from "../../utils/constants";
import { formatElapsed } from "../../utils/date";
import { getIncompleteBatchTables } from "../../utils/order";
import { getTableNumber, getTablePlace } from "../../utils/table";
import "./ToServeScreen.scss";

// "Listos para servir": platos y bebidas que cocina o bar ya marcó "Listo" y nadie ha servido, con
// cuánto llevan esperando (desde ready_at), el más antiguo primero. Es para
// quien dicta los pedidos y presiona a los mozos (pedido de JV, 2026-10-09).
// Clic en una mesa → su detalle, donde el mozo marca "Entregado".

const POLL_MS = 5000;
// Minutos con el plato listo sin llevar a partir de los cuales se pinta
// amarillo / rojo (la comida lista se enfría: umbrales más cortos que cocina).
const WARN_MINUTES = 3;
const LATE_MINUTES = 6;

const waitingMinutes = (readyAt, now) =>
  Math.max(0, Math.floor((now - new Date(readyAt).getTime()) / 60000));

export function ToServeScreen() {
  const { getOpenOrders } = useOrder();
  const history = useHistory();
  const now = useNow(15000);
  const [openOrders, setOpenOrders] = useState(null);
  const [offline, setOffline] = useState(false);

  const load = async () => {
    try {
      const response = await getOpenOrders();
      setOffline(false);
      setOpenOrders(response);
    } catch (error) {
      setOffline(true);
    }
  };
  usePolling(load, POLL_MS, { immediate: true });

  const readyOrders =
    openOrders &&
    filter(openOrders, (order) => order.status === ORDER_STATUS.READY && order.table);
  // WhatsApp / Venta rápida / Delivery salen en un solo lote: mientras algo
  // siga sin preparar, la tarjeta muestra "faltan N" en vez de pedir llevarlo.
  const incompleteTables = getIncompleteBatchTables(openOrders);
  const openByTable = groupBy(openOrders || [], "table");

  const cards = sortBy(
    map(groupBy(readyOrders || [], "table"), (orders) => {
      const tableOrders = filter(
        openByTable[orders[0].table],
        (order) => order.status !== ORDER_STATUS.DELIVERED
      );
      const readyAt = Math.min(
        ...map(orders, (order) => new Date(order.ready_at || order.created_at).getTime())
      );
      return {
        table: orders[0].table,
        number: getTableNumber(orders[0]),
        place: getTablePlace(orders[0]),
        readyAt,
        incomplete: incompleteTables.has(orders[0].table),
        readyCount: size(orders),
        totalCount: size(tableOrders),
        waiters: uniq(compact(map(orders, "created_by_name"))),
        lines: map(
          groupBy(orders, (order) => order.product_data?.title || "Producto eliminado"),
          (sameOrders, title) => `${size(sameOrders)} × ${title}`
        ),
      };
    }),
    "readyAt"
  );

  return (
    <div className="to-serve-screen">
      <HeaderPage title="Listos para servir" />
      <p className="to-serve-screen__hint">
        Platos y bebidas ya listos que todavía no se sirven, del más antiguo al más reciente.
      </p>

      {offline && (
        <Message warning>
          Sin conexión con el servidor — reintentando cada {POLL_MS / 1000} s.
        </Message>
      )}

      {readyOrders && size(cards) === 0 && (
        <div className="to-serve-screen__empty">
          <Icon name="check circle outline" /> No hay platos esperando.
        </div>
      )}

      <div className="to-serve-screen__list">
        {map(cards, (card) => {
          const minutes = waitingMinutes(card.readyAt, now);
          return (
            <div
              key={card.table}
              className={classNames("to-serve-screen__card", {
                incomplete: card.incomplete,
                warn: !card.incomplete && minutes >= WARN_MINUTES && minutes < LATE_MINUTES,
                late: !card.incomplete && minutes >= LATE_MINUTES,
              })}
              onClick={() => history.push(`/admin/table/${card.table}`)}
            >
              <div className="to-serve-screen__place">{card.place}</div>
              <div className="to-serve-screen__lines">{card.lines.join(" · ")}</div>
              <div className="to-serve-screen__meta">
                {card.incomplete ? (
                  <>
                    <Icon name="shopping bag" /> Sale en un solo lote: {card.readyCount} de{" "}
                    {card.totalCount} listos, faltan {card.totalCount - card.readyCount}
                  </>
                ) : (
                  <>
                    <Icon name="clock outline" /> Listo {formatElapsed(card.readyAt, now)}
                  </>
                )}
                {size(card.waiters) > 0 && (
                  <span className="to-serve-screen__waiter">
                    <Icon name="user outline" /> {card.waiters.join(", ")}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
